import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DeficiencyTypes } from '@core/enums/deficiency-types';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { NgClass, UpperCasePipe } from '@angular/common';
import { MatFabAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DeficiencyDetailsComponent } from '@shared/components/deficiency-details/deficiency-details.component';
import { MatFormField, MatPrefix, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { EnumToArrayPipe } from '@shared/pipes/enum-to-array.pipe';
import { Subject } from 'rxjs';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { DeficiencyService } from '../../services/deficiency.service';
import { DeficiencyFilterService } from '../../services/deficiency-filter.service';
import { DeficiencyTypeDisplayNamePipe } from '../../pipes/deficiency-type-display-name.pipe';
import { Deficiency } from '../../models/deficiency';

@Component({
  selector: 'app-inspection-request-deficiency-view',
  templateUrl: './inspection-request-deficiency-view.component.html',
  styleUrls: ['./inspection-request-deficiency-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFabAnchor,
    RouterLink,
    MatIcon,
    DeficiencyDetailsComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatPrefix,
    MatLabel,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatInput,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    UpperCasePipe,
    TranslateModule,
    EnumToArrayPipe,
    DeficiencyTypeDisplayNamePipe,
  ],
})
export class InspectionRequestDeficiencyViewComponent implements OnInit, OnDestroy {
  #deficiencyDashboardService = inject(DeficiencyService);
  #filterService = inject(DeficiencyFilterService);
  #route = inject(ActivatedRoute);
  #spinnerService = inject(SpinnerService);
  #translate = inject(TranslateService);
  #formBuilder = inject(FormBuilder);

  @Input() public isProjectPage = false;
  @Input() public inspectionRequestId: string;
  @ViewChild(MatSort) public deficiencyDetailsSort: MatSort;
  public deficiencyDetailsTableColumns: string[] = ['deficiencyType', 'equipmentId', 'cml', 'finding'];
  public selection = new SelectionModel<Deficiency>(true, []);
  public deficiencyDetailsDataSource: MatTableDataSource<Deficiency>;
  public inspectionRequestName: string;
  public filterFormGroup: FormGroup;
  public deficiencyTypes = DeficiencyTypes;
  public deficiencyPipe = new DeficiencyTypeDisplayNamePipe(this.#translate);

  #visualEventsCategories: DeficiencyTypes[] = [
    DeficiencyTypes.Emergency,
    DeficiencyTypes.High,
    DeficiencyTypes.Routine,
    DeficiencyTypes.Standard,
    DeficiencyTypes.Observation,
  ];
  readonly #destroy$ = new Subject();

  public get totalUtAlarmCount(): number {
    return this.totalCountOf([DeficiencyTypes.UTAlarm]);
  }

  public get totalRedliningCount(): number {
    return this.totalCountOf([DeficiencyTypes.Redlining]);
  }

  public get totalVisualEventsCount(): number {
    return this.totalCountOf(this.#visualEventsCategories);
  }

  public get totalEmergencyCounter(): number {
    return this.totalCountOf([DeficiencyTypes.Emergency]);
  }

  public get totalHighCounter(): number {
    return this.totalCountOf([DeficiencyTypes.High]);
  }

  public get totalStandardCounter(): number {
    return this.totalCountOf([DeficiencyTypes.Standard]);
  }

  public get totalRoutineCounter(): number {
    return this.totalCountOf([DeficiencyTypes.Routine]);
  }

  public get totalObservationCounter(): number {
    return this.totalCountOf([DeficiencyTypes.Observation]);
  }

  public get categoriesFormControl(): FormControl {
    return this.filterFormGroup.get('categories') as FormControl;
  }

  public get searchTextFormControl(): FormControl {
    return this.filterFormGroup.get('searchText') as FormControl;
  }

  public get selectDeficiencyTriggerText(): string {
    const allSelected = this.categoriesFormControl.value.includes('All');
    let selectedValueCount = this.categoriesFormControl.value.length;
    const showAllText = selectedValueCount === Object.keys(this.deficiencyTypes).length + 1;
    let triggerText = '';
    if (selectedValueCount === 0) {
      return '';
    }
    if (showAllText) {
      triggerText = this.#translate.instant('dashboard.All');
    } else if (allSelected) {
      if (selectedValueCount === 1) {
        return '';
      } else {
        triggerText = this.deficiencyPipe.transform(this.categoriesFormControl.value[1]);
      }
    } else {
      triggerText = this.deficiencyPipe.transform(this.categoriesFormControl.value[0]);
    }
    triggerText += ' (' + this.totalCountOf(showAllText ? Object.keys(this.deficiencyTypes) : [this.categoriesFormControl.value[0]]) + ')';
    selectedValueCount -= allSelected ? 1 : 0;
    if (selectedValueCount > 1 && !showAllText) {
      triggerText += ' ...';
    }
    return triggerText;
  }

  public ngOnInit(): void {
    this.#spinnerService.show(this.#spinnerService.mainSpinnerId);
    this.initForm();
    this.initData();
    this.initState();
  }

  public ngOnDestroy(): void {
    this.#destroy$.next(null);
    this.#destroy$.complete();
  }

  public selectAll(selected: boolean): void {
    if (selected) {
      this.categoriesFormControl.patchValue(['All', ...Object.keys(DeficiencyTypes)]);
    } else {
      this.categoriesFormControl.patchValue([]);
    }
    this.categoriesFormControl.updateValueAndValidity({ onlySelf: true });
  }

  public checkAllSelected() {
    if (this.categoriesFormControl.value.length < Object.keys(this.deficiencyTypes).length + 1) {
      const allIndex = this.categoriesFormControl.value.indexOf('All');
      if (allIndex !== -1) {
        this.categoriesFormControl.value.splice(allIndex, 1);
        this.categoriesFormControl.patchValue(this.categoriesFormControl.value);
      }
      if (this.categoriesFormControl.value.length === Object.keys(this.deficiencyTypes).length && allIndex === -1) {
        this.categoriesFormControl.patchValue(['All', ...Object.keys(DeficiencyTypes)]);
      }
    }
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.deficiencyDetailsDataSource.filteredData.length;
    return numSelected === numRows;
  }

  public masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.deficiencyDetailsDataSource.filteredData.forEach(row => this.selection.select(row));
    }
  }

  protected totalCountOf(categories: (DeficiencyTypes | string)[]): number {
    return this.deficiencyDetailsDataSource
      ? this.deficiencyDetailsDataSource.data.reduce(
          (prev, curr) =>
            prev +
            categories.reduce(
              (p, c) =>
                p + (curr.deficiencyType.toLocaleLowerCase().replace(/\s/g, '') === c.toLocaleLowerCase().replace(/\s/g, '') ? 1 : 0),
              0,
            ),
          0,
        )
      : 0;
  }

  protected filter(criteria: { searchText: string; categories: DeficiencyTypes[] }): void {
    this.#filterService.filterDeficiencyDetailsDataSource(criteria, this.deficiencyDetailsDataSource);
  }

  protected initForm(): void {
    this.filterFormGroup = this.#formBuilder.group({
      searchText: this.#formBuilder.control<string>('', { nonNullable: true }),
      categories: this.#formBuilder.control<DeficiencyTypes[]>([], { nonNullable: true }),
    });
    this.filterFormGroup.valueChanges
      .pipe(takeUntil(this.#destroy$))
      .subscribe((criteria: { searchText: string; categories: DeficiencyTypes[] }) => {
        this.filter(criteria);
      });
  }

  protected initData(): void {
    if (!this.isProjectPage) {
      this.inspectionRequestId = this.#route.snapshot.paramMap.get('inspectionRequestId');
      this.inspectionRequestName =
        this.#route.snapshot.queryParamMap.get('inspectionRequestName') ?? this.getInspectionRequestNameFromUrl();
    } else {
      this.deficiencyDetailsTableColumns.unshift('select');
    }
    this.#deficiencyDashboardService.getDeficiencies(this.inspectionRequestId).subscribe(items => {
      this.deficiencyDetailsDataSource = new MatTableDataSource<Deficiency>(items);
      this.deficiencyDetailsDataSource.sort = this.deficiencyDetailsSort;
      this.initState();
      this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
    });
  }

  protected initState(): void {
    this.categoriesFormControl.patchValue(['All', ...Object.keys(DeficiencyTypes)]);
    this.categoriesFormControl.updateValueAndValidity({ onlySelf: true });
  }

  private getInspectionRequestNameFromUrl(): string {
    return this.#route.snapshot.url.length === 1 && this.#route.snapshot.url[0].path.includes('inspectionRequestName=')
      ? this.#route.snapshot.url[0].path.split('inspectionRequestName=')[1]
      : '';
  }
}
